import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SasDynamicFormService } from '@shared/sas-dynamic-form/sas-dynamic-form.service';
import { SasLabelValueComponent } from '@shared/sas-label-value/sas-label-value.component';
import { FormSubmittedService } from '@shared/services/form-submitted.service';

@Component({
    selector: 'sas-radiobutton-field-type',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormlyModule, TranslateModule, SasLabelValueComponent],
    providers: [],
    template: `
        @if (isShowingSummaryInfo) {
            <sas-label-value [label]="field.props.label ?? ''"
                             [model]="radioValue"
                             sasClass="textmode toplabel mb-2">
            </sas-label-value>
        } @else {
            <div class="form-group-radio mb-3">
                <fieldset class="sas-radiobutton"
                          [attr.aria-describedby]="(field.props.label ? field.id + '-group-label' : '') + (field.formControl.errors?.['required'] ? field.id + 'Desc' : '')">
                    <legend class="radio-legend fw-semibold" [id]="field.id + '-group-label'">
                        @if (field.props.label) {
                        {{ field.props.label | translate }}
                        }
                        @if (field.props.required) {
                        <span aria-hidden="true"> *</span>
                        }
                        <div [title]="helpText" class="sas-inline-block" [id]="field.id + '-help'">
                            @if (helpText.length && !isReadonly) {
                            <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                            }
                        </div>
                    </legend>
                    @for (val of sasValues; track $index) {
                    <span class="radio-option"
                         [ngClass]="{
                            'd-block mr-auto ml-auto': field.props['orientation'] === 'vertical',
                            'is-invalid' : field.formControl.invalid && (field.formControl.dirty || formSubmittedService.isSubmitted())
                         }"
                    >
                        <input type="radio"
                               class="radio-input"
                               [id]="field.name + '-' + $index"
                               [formControl]="formControl"
                               name="{{field.name}}"
                               [value]="val"
                               required="{{field.props.required}}"/>
                        <label class="radio-label" [for]="field.name + '-' + $index">
                            {{ sasLabels[$index] | translate }}
                        </label>
                    </span>
                    }
                    <div class="invalid-feedback"
                         [id]="field.id + 'Desc'"
                         role="alert"
                         aria-atomic="true">
                        @if (field.formControl.errors?.['required']) {
                        <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
                        }
                    </div>
                </fieldset>
            </div>
        }
    `,
    styleUrl: './sas-radiobutton-field-type.component.scss'
})

export class SasRadiobuttonFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    private translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);

    helpText: string = '';
    sasLabels: any = [];
    sasValues: any = [];
    isShowingSummaryInfo: boolean = false;

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    get radioValue(): any {
        const { value } = this.formControl;
        if (!value) return '';

        const index = this.sasValues.indexOf(value);
        return index === -1 ? value : this.sasLabels[index];
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        this.sasLabels = this.convertToArray(this.field.props['labels']) || [];
        this.sasValues = this.convertToArray(this.field.props['values']) || [];
        this.isShowingSummaryInfo = this.field.props['isShowingSummaryInfo'] ?? false;
    }

    ngDoCheck() {
        if (this.isReadonly && !this.formControl.disabled) {
            this.formControl.disable({ emitEvent: false });
        }

        if (!this.isReadonly && this.formControl.disabled) {
            this.formControl.enable({ emitEvent: false });
        }
    }

    convertToArray(value: string) {
        const array = value.replace(/'/g, '"') //replacing all ' with "
        return JSON.parse(array);
    }
}
